import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters, internalPath } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import usePackWallets from "/imports/hooks/usePackWallets";
import { saveResumeIntent } from "/imports/libs/resumeIntent";
import { collectWeeklySlots, weeklyRepeatMessage } from "/imports/libs/weeklyRepeat";
import { clubRulesForCategoryType } from "/imports/libs/bookingRules";
import { packWindowMessage } from "/imports/libs/packWindowMessage";
import { packMemberPricing, memberPriceNote } from "/imports/libs/memberPricing";
import useClubBookingCancel from "/imports/hooks/useClubBookingCancel";

// Booking dates are fake UTC (club wall clock labeled Z).
const wall = (iso) => DateTime.fromISO(iso, { zone: "utc" });

// Same window shape as the server: a 1-month validity starting Jan 1 ends
// Jan 31, not Feb 1.
const windowEnd = (startDay, dur, unit) => startDay.plus({ [unit]: dur }).minus({ days: 1 }).endOf("day");

const COMPOSER_CATEGORY_TYPES = ["category-service-private", "category-service-court"];

/**
 * Pack-composition mode for the Lessons / Courts booking pages.
 *
 * When the URL carries ?packProduct=…&packVariant=…, the page turns from
 * book-one-slot into compose-the-pack: slot taps toggle into a selection that
 * survives date/trainer switching, a bottom tray tracks progress, and
 * Continue purchases the package with the picks as upfrontSlots (the server
 * holds each one as a freshly-created PENDING booking while paying).
 *
 * Owned here so both booking hooks share one implementation — and so a later
 * reverse flow (compose repeat bookings first, get offered the pack) can
 * reuse the same machinery with a different entry point.
 */
export default function usePackComposer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency, saas, locations } = useWidget();
  const { isAuthenticated } = useAuth();
  const { filters, setFilter } = useUrlFilters({});

  const productId = filters.packProduct || null;
  const variantKey = filters.packVariant || null;
  const backTo = internalPath(filters.from);

  // The catalog is the authority on the variant (mode, anchor, expiry, price) —
  // never trust what a crafted URL claims about it.
  const { data: catalog, loading } = useFetch(() => api.listPackagesForSale(slug), [slug], { skip: !slug || !productId });
  const pkg = (catalog?.items || []).find((p) => p._id === productId) || null;
  const opt = (pkg?.pricingOptions || []).find((o) => o.variantKey === variantKey) || null;

  const mode = opt?.upfrontBooking;
  const active =
    !!productId && (loading || (!!opt && (mode === "all" || mode === "partial") && COMPOSER_CATEGORY_TYPES.includes(opt.linkedCategoryType)));
  const resolved = active && !loading && !!opt;

  const isCourt = opt?.linkedCategoryType === "category-service-court";
  const sessionsN = opt?.sessions || 0;
  const anchor = opt?.upfrontAnchor === "saleDate" ? "saleDate" : "firstSession";
  const minPicks = mode === "all" ? sessionsN : 1;
  const windowCfg =
    opt && !opt.noExpiry && opt.expiryDuration && opt.expiryDurationUnit ? { dur: opt.expiryDuration, unit: opt.expiryDurationUnit } : null;

  const { data: memberStatus } = useFetch(() => api.getPurchaseMembershipStatus(slug), [slug], { skip: !slug || !resolved || !isAuthenticated });

  const [picks, setPicks] = useState([]);
  // Weeks the repeat stepped over because the trainer/court was taken. They are
  // a UI record of what the server never offered, not slots — the client taps
  // one to land on that day and choose another time.
  const [blockedWeeks, setBlockedWeeks] = useState([]);
  const [repeatBusy, setRepeatBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  // Lessons the club itself put on this client's calendar and never charged
  // for. The pack can pay for those instead of the client composing a fresh
  // slot and leaving the lesson unpaid. Courts are excluded: a court the club
  // booked is its own arrangement, and the server refuses the ids anyway.
  const { data: claimable } = useFetch(
    () =>
      api.listClaimableSessions(slug, {
        productId,
        pricingId: opt?.pricingId,
        variantKey,
        sessions: opt?.sessions,
        sessionDuration: opt?.sessionDuration,
        linkedServiceId: opt?.linkedServiceId,
        locationId: filters.packLocation || undefined
      }),
    [slug, productId, variantKey, filters.packLocation],
    { skip: !slug || !resolved || isCourt || !isAuthenticated }
  );
  // null until the arrival tick has run — an empty Set means "the client has
  // unticked everything", which must not be re-ticked on the next render.
  const [claimedIds, setClaimedIds] = useState(null);
  // Lessons checkout refused: billed at the desk, or cancelled, since the list
  // was fetched. They are gone as far as this pack is concerned, so they must
  // not come back as something the client can tap on again.
  const [rejectedIds, setRejectedIds] = useState(() => new Set());
  // Trainer + location to open the slot grid on, when every lesson ticked on
  // arrival shares both — the rest of the pack is most likely more of the same.
  // Set once, by the arrival tick; the booking page applies it once.
  const [arrivalFilters, setArrivalFilters] = useState(null);
  const [existingTicked, setExistingTicked] = useState(false);

  const today = useMemo(() => DateTime.now().startOf("day"), []);

  const byStart = (a, b) => (a.startDate < b.startDate ? -1 : 1);

  // One pack, one validity window. A saleDate pack is measured from the day it
  // is bought, so even the earliest session can fall outside it; a firstSession
  // pack is measured from the earliest session the purchase holds, so nothing
  // bounds that first one. A session past the deadline is one this pack can
  // never pay for.
  const validityDeadline = (firstISO) => {
    if (!windowCfg) return null;
    if (anchor === "saleDate") return windowEnd(today, windowCfg.dur, windowCfg.unit);
    if (!firstISO) return null;
    return windowEnd(wall(firstISO).startOf("day"), windowCfg.dur, windowCfg.unit);
  };

  // A club-placed lesson wears the same shape as a composed pick so the tray,
  // the counts and the window rules can treat one list.
  const claimedSessions = useMemo(
    () =>
      (claimable?.items || [])
        .filter((c) => claimedIds?.has(c.bookingClientId))
        .map((c) => ({
          key: `claimed:${c.bookingClientId}`,
          claimed: true,
          bookingClientId: c.bookingClientId,
          bookishId: c.bookishId,
          date: c.date,
          time: c.time,
          startDate: c.startDate,
          trainerId: c.trainerId || null,
          trainerName: c.trainerName || null,
          facilityId: c.facilityId || null,
          locationId: c.locationId,
          locationName: c.locationName || null
        }))
        .sort(byStart),
    [claimable, claimedIds]
  );

  // Everything this purchase commits to, in date order. The server counts both
  // lists against the variant's demand and spans the validity window across
  // them, so every rule on this page is asked of this, never of picks alone.
  const committed = useMemo(() => [...picks, ...claimedSessions].sort(byStart), [picks, claimedSessions]);

  // A book-all pack is member-priced only while every session of the purchase
  // — the club's lessons included — sits at a branch the client is a member at.
  const memberPricing = packMemberPricing({
    pkg,
    opt,
    memberStatus,
    purchaseLocationId: filters.packLocation || null,
    sessionLocationIds: committed.map((c) => c.locationId || filters.packLocation || null)
  });
  const effectivePrice = memberPricing.price;
  const memberNote = memberPriceNote({
    pricing: memberPricing,
    opt,
    currency,
    nameOf: (id) => (locations || []).find((l) => l._id === id)?.name
  });

  // Date cap from the pack's validity window — merged by the booking hooks
  // with the service's own booking-window cap.
  const packMaxDate = useMemo(() => {
    if (!windowCfg) return null;
    if (anchor === "saleDate") return windowEnd(today, windowCfg.dur, windowCfg.unit).toISODate();
    if (committed.length > 0) return windowEnd(wall(committed[0].startDate).startOf("day"), windowCfg.dur, windowCfg.unit).toISODate();
    return null;
  }, [windowCfg, anchor, committed, today]);

  // Named on every availability call in pack mode, so the server knows it is
  // serving a PURCHASE — a pack the client already owns has no say over these
  // slots. packFirstDate is what buys the dates past the club's window, and only
  // exists once a first session is picked.
  const packAvailabilityParams =
    pkg && opt
      ? { packProduct: pkg._id, packVariant: opt.variantKey, ...(committed.length > 0 ? { packFirstDate: committed[0].date } : {}) }
      : null;

  const windowMessage = () =>
    packWindowMessage({
      firstISO: committed[0].startDate,
      deadline: windowEnd(wall(committed[0].startDate).startOf("day"), windowCfg.dur, windowCfg.unit),
      dur: windowCfg.dur,
      unit: windowCfg.unit,
      noun: isCourt ? "session" : "lesson"
    });

  const isPicked = (date, time) => picks.some((p) => p.date === date && p.time === time);

  // Would adding a session on this day keep every pick inside one validity
  // window? (firstSession anchor: an EARLIER pick moves the window start back.)
  const fitsWindow = (dateISO) => {
    if (!windowCfg || anchor !== "firstSession" || committed.length === 0) return true;
    const d = DateTime.fromISO(dateISO, { zone: "utc" });
    const first = wall(committed[0].startDate).startOf("day");
    const last = wall(committed[committed.length - 1].startDate);
    const start = d < first ? d.startOf("day") : first;
    const end = d.endOf("day") > last ? d.endOf("day") : last;
    return end <= windowEnd(start, windowCfg.dur, windowCfg.unit);
  };

  // Would this club-placed lesson fit the pack's validity alongside everything
  // already in the purchase?
  const claimFits = (item) => {
    const deadline = anchor === "saleDate" ? validityDeadline() : null;
    return deadline ? wall(item.startDate) <= deadline : fitsWindow(item.date);
  };

  // Club-placed lessons this pack could pay for but currently isn't: ones that
  // didn't fit the count on arrival, and ones the client left out. Only those
  // inside the pack's validity — the club books within the pack's expiry, and
  // one past it could never be added. The slot grid can never show them (the
  // lesson itself is what makes that time unavailable), so the tray is the
  // only place they can be offered back.
  const unclaimed = claimedIds
    ? (claimable?.items || [])
        .filter((c) => !claimedIds.has(c.bookingClientId) && !rejectedIds.has(c.bookingClientId) && claimFits(c))
        .sort(byStart)
    : [];

  const toPick = (slot, date) => ({
    key: `${date}|${slot.time}`,
    date,
    time: slot.time,
    startDate: slot.startDate,
    trainerId: slot.trainerId || null,
    trainerName: slot.trainerName || null,
    facilityId: slot.facilityId || null,
    facilityName: slot.facilityName || null,
    locationId: slot.locationId,
    locationName: slot.locationName || null
  });

  // Lessons the club booked and never charged for are ticked on arrival,
  // earliest first, so a client who was booked three lessons ago lands on a
  // part-filled pack and only has to compose the rest. Once: unticking is
  // theirs to do, and the pack is still theirs to spend on other days.
  useEffect(() => {
    if (claimedIds !== null || !resolved || !claimable) return;

    const offered = [...(claimable.items || [])].sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
    if (offered.length === 0) {
      setClaimedIds(new Set());
      return;
    }

    const kept = [];
    let pastValidity = 0;
    for (const c of offered) {
      if (kept.length >= sessionsN) break;
      const deadline = validityDeadline(kept[0]?.startDate);
      if (deadline && wall(c.startDate) > deadline) {
        pastValidity += 1;
        continue;
      }
      kept.push(c);
    }

    setClaimedIds(new Set(kept.map((c) => c.bookingClientId)));
    if (kept.length > 0) setExistingTicked(true);
    const first = kept[0];
    if (first?.trainerId && first.locationId && kept.every((c) => c.trainerId === first.trainerId && c.locationId === first.locationId)) {
      setArrivalFilters({ trainerId: first.trainerId, locationId: first.locationId });
    }
    if (kept.length === 0 && pastValidity === 0) return;

    // Said out loud, or the client pays and finds lessons of theirs still
    // unpaid with nothing on the page saying which, or why.
    if (kept.length === 0) {
      enqueueSnackbar(
        `The club booked you ${pastValidity} lesson${pastValidity === 1 ? "" : "s"} further out than this pack reaches — this pack can't pay for ${
          pastValidity === 1 ? "it" : "them"
        }`,
        { variant: "warning" }
      );
      return;
    }

    const ticked =
      kept.length === 1
        ? "The club already booked you a lesson — we've ticked it so this pack pays for it"
        : `The club already booked you ${kept.length} lessons — we've ticked them so this pack pays for them`;
    const trimmed =
      pastValidity > 0
        ? `. ${pastValidity} more ${pastValidity === 1 ? "is" : "are"} further out than one pack reaches — ${
            pastValidity === 1 ? "it needs" : "they need"
          } a second pack`
        : "";
    enqueueSnackbar(`${ticked}${trimmed}`, { variant: "info" });
  }, [claimable, resolved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Slot tap in pack mode: toggle. Same date+time unpicks regardless of which
  // trainer/court row was tapped — one session per time slot.
  const togglePick = (slot, date) => {
    if (!resolved) return; // catalog still loading — the cap/window rules aren't known yet
    const key = `${date}|${slot.time}`;
    if (picks.some((p) => p.key === key)) {
      setPicks((prev) => prev.filter((p) => p.key !== key));
      return;
    }
    if (committed.length >= sessionsN) {
      enqueueSnackbar(`This pack covers ${sessionsN} sessions — unselect one first`, { variant: "info" });
      return;
    }
    if (!fitsWindow(date)) {
      enqueueSnackbar(windowMessage(), { variant: "warning" });
      return;
    }
    setPicks((prev) => [...prev, toPick(slot, date)].sort(byStart));
    setBlockedWeeks((prev) => prev.filter((b) => b.date !== date));
  };

  const removePick = (key) => setPicks((prev) => prev.filter((p) => p.key !== key));

  // Untick a lesson the club booked: it stays on the calendar exactly as it is,
  // the pack simply stops paying for it.
  const removeClaimed = (bookingClientId) =>
    setClaimedIds((prev) => {
      const next = new Set(prev);
      next.delete(bookingClientId);
      return next;
    });

  // A cancelled lesson frees its trainer's time, and the client was told they
  // can pick it again — the slot grid has to show it.
  const [slotsVersion, setSlotsVersion] = useState(0);

  const clubCancel = useClubBookingCancel({
    onCancelled: ({ bookingClientId }) => {
      removeClaimed(bookingClientId);
      setRejectedIds((prev) => new Set(prev).add(bookingClientId));
      setSlotsVersion((v) => v + 1);
    },
    onLeftOut: ({ bookingClientId }) => removeClaimed(bookingClientId)
  });

  const openLessonCancel = (item, outsidePack) =>
    clubCancel.openFor({
      kind: "lesson",
      outsidePack,
      bookingClientId: item.bookingClientId,
      bookishId: item.bookishId,
      label: `${wall(item.startDate).toFormat("ccc d LLL")} at ${item.time}${item.trainerName ? ` with ${item.trainerName}` : ""}`
    });

  // ✕ on a lesson the club booked asks to cancel it rather than quietly leaving
  // it on the calendar unpaid.
  const requestRemoveClaimed = (bookingClientId) => {
    const item = claimedSessions.find((c) => c.bookingClientId === bookingClientId);
    if (item) openLessonCancel(item, false);
  };

  // The ones this pack isn't paying for are just as unpaid — a client who won't
  // be around in December can drop them here instead of hunting for them on
  // the dashboard.
  const requestCancelUnclaimed = (item) => openLessonCancel(item, true);

  // Tick a club-placed lesson back on. The count and window rules are the picks'
  // own, so a lesson can only come back to a purchase that still has room for it
  // and still reaches it.
  const restoreClaimed = (item) => {
    if (!resolved || claimedIds?.has(item.bookingClientId)) return;
    if (committed.length >= sessionsN) {
      enqueueSnackbar(`This pack covers ${sessionsN} sessions — unselect one first`, { variant: "info" });
      return;
    }
    if (!claimFits(item)) {
      enqueueSnackbar(anchor === "saleDate" ? "This lesson is after your pack's validity ends" : windowMessage(), { variant: "warning" });
      return;
    }
    setClaimedIds((prev) => new Set(prev).add(item.bookingClientId));
  };

  // Lessons the club booked stay: removing one cancels it, which is asked one
  // at a time, never swept away with the client's own picks.
  const clearPicks = () => {
    setPicks([]);
    setBlockedWeeks([]);
  };

  const dismissBlockedWeek = (date) => setBlockedWeeks((prev) => prev.filter((b) => b.date !== date));

  // Tapping a blocked week moves the page to that day so the client can see for
  // themselves that the time is gone and take another one. The placeholder is
  // spent once tapped: it has done its job of being noticed, and Continue goes
  // back to whatever the pack's own rule says.
  const goToBlockedWeek = (week) => {
    dismissBlockedWeek(week.date);
    setFilter("date", week.date);
    enqueueSnackbar(`${week.trainerName || "That slot"} isn't free at ${week.time} on ${wall(week.date).toFormat("ccc d LLL")} — pick another time`, {
      variant: "warning"
    });
  };

  // Fill the remaining sessions with the last session's weekday/time (same
  // trainer for lessons, same-court-preferred for courts), checking real
  // availability week by week and leaving the weeks that are taken as holes.
  //
  // The pattern is read off the whole purchase, so a client whose only session
  // so far is the lesson the club booked can repeat straight from it.
  const repeatWeekly = async () => {
    const last = committed[committed.length - 1];
    if (!last || committed.length >= sessionsN || repeatBusy) return;
    setRepeatBusy(true);

    const deadline = windowCfg
      ? windowEnd(anchor === "firstSession" ? wall(committed[0].startDate).startOf("day") : today, windowCfg.dur, windowCfg.unit)
      : null;

    const findSlot = async (dateISO) => {
      const params = {
        date: dateISO,
        serviceId: opt.linkedServiceId,
        duration: opt.sessionDuration,
        locationId: last.locationId || undefined,
        // The weeks being walked can sit past the public booking window — ask
        // with the same extension the grid is drawn with.
        ...(packAvailabilityParams || {})
      };
      const res = isCourt
        ? await api.getCourtAvailability(slug, params)
        : await api.getLessonAvailability(slug, { ...params, trainerId: last.trainerId || undefined });
      const slots = res?.slots || [];
      return (
        slots.find((s) => s.time === last.time && (isCourt ? s.facilityId === last.facilityId : s.trainerId === last.trainerId)) ||
        slots.find((s) => s.time === last.time) ||
        null
      );
    };

    const { matches, skippedDates } = await collectWeeklySlots({
      fromDate: last.date,
      deadline,
      need: sessionsN - committed.length,
      findSlot
    });
    setRepeatBusy(false);

    if (matches.length === 0) {
      enqueueSnackbar("No matching weekly slots found ahead", { variant: "info" });
      return;
    }
    const added = matches.map((m) => toPick(m.slot, m.date));
    setPicks((prev) => [...prev, ...added].sort(byStart));
    setBlockedWeeks(
      skippedDates.map((date) => ({
        date,
        time: last.time,
        trainerName: isCourt ? last.facilityName : last.trainerName || null
      }))
    );
    const remaining = sessionsN - committed.length - added.length;
    enqueueSnackbar(weeklyRepeatMessage({ addedN: added.length, skipped: skippedDates.length, remaining, unit: isCourt ? "session" : "lesson" }), {
      variant: remaining > 0 ? "info" : "success"
    });
  };

  const countOk = committed.length >= minPicks && committed.length <= sessionsN;
  // Payment waits on every hole being cleared off the list — by tapping it, or
  // by dismissing it. Either way the client has seen the week that could not be
  // booked, and from there the pack's own rule decides: an `all` pack still
  // wants its full count, a `partial` one is free to go.
  const canContinue = countOk && blockedWeeks.length === 0;

  const wallets = usePackWallets({ open: confirmOpen, locationId: filters.packLocation, price: effectivePrice });

  const doPurchase = async ({ voucherId } = {}) => {
    if (!isAuthenticated) {
      const page = isCourt ? "courts" : "lessons";
      enqueueSnackbar("Please sign in to complete your purchase", { variant: "warning" });
      navigate(`/${slug}/login?redirect=${encodeURIComponent(`${page}${location.search}`)}`);
      return;
    }
    setBuying(true);
    try {
      const result = await api.purchasePackage(slug, {
        productId: pkg._id,
        pricingId: opt.pricingId,
        variantKey: opt.variantKey,
        sessions: opt.sessions,
        sessionDuration: opt.sessionDuration,
        linkedServiceId: opt.linkedServiceId,
        locationId: filters.packLocation || undefined,
        // Two lists, one purchase: slots to book now, and lessons the club
        // already put on the calendar that this pack is paying for. The server
        // counts them together against the variant's demand.
        upfrontSlots: picks.map((p) => ({
          date: p.date,
          time: p.time,
          trainerId: p.trainerId || undefined,
          facilityId: p.facilityId || undefined,
          locationId: p.locationId
        })),
        upfrontBcIds: claimedSessions.map((c) => c.bookingClientId),
        voucherId: voucherId || undefined
      });
      if (result.chargeId) {
        sessionStorage.setItem("widget_slug", slug);
        if (backTo) saveResumeIntent({ slug, backTo });
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }
      enqueueSnackbar("Purchase complete — your sessions are booked", { variant: "success" });
      navigate(`/${slug}/dashboard?tab=bookings&scope=upcoming`);
    } catch (err) {
      setConfirmOpen(false);
      // A pick that was just taken comes back with its "date|time" key — drop
      // it so the client replaces exactly that one. A club-placed lesson that
      // stopped being claimable (billed at the desk, cancelled) comes back by
      // its BookingClient id and is unticked the same way.
      const conflictKey = err.data?.data?.slotKey;
      if (conflictKey) removePick(conflictKey);
      const conflictBcId = err.data?.data?.bookishClientId;
      if (conflictBcId) {
        removeClaimed(conflictBcId);
        setRejectedIds((prev) => new Set(prev).add(conflictBcId));
      }
      enqueueSnackbar(err.message || "Failed to start purchase", { variant: "error" });
    } finally {
      setBuying(false);
    }
  };

  // The club's rules for this pack's category, shown in the picker and again in
  // the confirm dialog the client ticks to accept them.
  const packRules = opt ? clubRulesForCategoryType(saas?.widgetRules, opt.linkedCategoryType) : [];

  return {
    active,
    resolved,
    packRules,
    loading: active && loading,
    pkg,
    opt,
    isCourt,
    mode,
    sessionsN,
    minPicks,
    anchor,
    currency,
    effectivePrice,
    regularPrice: memberPricing.regularPrice,
    memberNote,
    backTo,
    packLocationId: filters.packLocation || null,
    packMaxDate,
    // True once the pack's validity, not the club's booking window, is what
    // bounds the remaining picks. A lesson the club booked counts: it is a
    // session of this purchase and it anchors the window like any other.
    hasFirstPick: committed.length > 0,
    packAvailabilityParams,
    arrivalFilters,
    existingTicked,
    picks,
    // The whole purchase, picks and club-placed lessons interleaved by date —
    // what the tray counts, lists and reads its dates off.
    committed,
    claimedCount: claimedSessions.length,
    // Club-placed lessons this purchase is NOT paying for — offered back in the
    // tray, because no slot grid can show a time its own booking has taken.
    unclaimed,
    blockedWeeks,
    isPicked,
    togglePick,
    removePick,
    requestRemoveClaimed,
    requestCancelUnclaimed,
    clubCancel,
    slotsVersion,
    restoreClaimed,
    clearPicks,
    dismissBlockedWeek,
    goToBlockedWeek,
    goToDate: (iso) => setFilter("date", iso),
    repeatWeekly,
    repeatBusy,
    countOk,
    canContinue,
    confirmOpen,
    wallets,
    setConfirmOpen,
    buying,
    doPurchase
  };
}
